import React, {
  createContext,
  ReactElement,
  ReactNode,
  useContext,
} from 'react'
import { Form, FormInstance, FormItemProps, RowProps } from 'antd'
import styled from 'styled-components'

import LayoutFormAnchor from './layout/LayoutFormAnchor'
import LayoutFormScrollable from './layout/LayoutFormScrollable'
import LayoutFormScrollableDivider from './layout/LayoutFormScrollableDivider'
import LayoutFormSimple from './layout/LayoutFormSimple'
import { removeFragmentFromChildren } from './utils'
import { FormFn, TCreateAntdFormElementsFormProps } from '../antdform-kit-v2'

const Styled = styled.div`
  height: 100%;

  .ant-form-item {
    margin: 0;
  }

  & .ant-form-item-label {
    padding-bottom: 4px;
  }
`

export type TFormVariant = 'basic' | 'padded'

export type TFormLayout = 'anchor' | 'scroll' | 'simple' | 'scroll-divider'

type FormFnUIContext = {
  variant: TFormVariant
  formId: string
  createMode: boolean
  disabled?: boolean
}

const Context = createContext<FormFnUIContext>({
  variant: 'padded',
  formId: '',
  createMode: true,
  disabled: false,
})

export const useFormFnUIContext = () => useContext(Context)

type RenderChildren = (
  form: Omit<FormInstance, 'scrollToField' | 'getFieldInstance'>
) => ReactNode[]

export type FormUIProps = Omit<
  TCreateAntdFormElementsFormProps,
  'children' | 'variant'
> & {
  variant?: TFormVariant
  footer?: ReactNode
  wrapLayout?: TFormLayout
  dependencies?: FormItemProps['dependencies']
  children?: RenderChildren | ReactNode
  anchorListTitle?: ReactNode
  wrapperStyle?: React.CSSProperties
  layoutRowProps?: RowProps
}

const isChildFunction = (
  child: ReactNode | RenderChildren
): child is RenderChildren => {
  return typeof child === 'function'
}

const layouts = {
  'scroll-divider': LayoutFormScrollableDivider,
  simple: LayoutFormSimple,
  scroll: LayoutFormScrollable,
  anchor: LayoutFormAnchor,
}

export const FormUI = ({
  children,
  footer,
  variant = 'padded',
  wrapLayout = 'scroll',
  formId,
  createMode = true,
  disabled,
  dependencies,
  anchorListTitle,
  wrapperStyle,
  layoutRowProps,
  ...props
}: FormUIProps) => {
  const Layout = layouts[wrapLayout]

  return (
    <Context.Provider value={{ variant, formId, createMode, disabled }}>
      <Styled style={wrapperStyle}>
        <FormFn
          loadingSkeletonProps={{ style: { padding: 16 } }}
          layout="vertical"
          className="fill-height"
          formId={formId}
          createMode={createMode}
          {...props}
        >
          {isChildFunction(children) || dependencies ? (
            <Form.Item noStyle dependencies={dependencies}>
              {(form) => (
                <Layout
                  footer={footer}
                  anchorListTitle={anchorListTitle}
                  rowProps={layoutRowProps}
                >
                  {isChildFunction(children)
                    ? removeFragmentFromChildren(
                        children(form) as unknown as ReactElement
                      )
                    : children}
                </Layout>
              )}
            </Form.Item>
          ) : (
            <Layout
              footer={footer}
              anchorListTitle={anchorListTitle}
              rowProps={layoutRowProps}
            >
              {children}
            </Layout>
          )}
        </FormFn>
      </Styled>
    </Context.Provider>
  )
}

export default FormUI
